import React, { useMemo } from 'react';
import { PartyPopper, Cake } from 'lucide-react';

/**
 * Full-width birthday banner shown on an agent's profile whenever the
 * current ET date matches their `date_of_birth` (MM-DD). Visible to
 * both KR admins reviewing the agent AND the agent themselves — the
 * copy stays personal ("Happy Birthday, Alex!") in both views.
 *
 * The date comparison runs in America/New_York so it flips at
 * midnight ET, matching the backend `services/date_utils.today_et`
 * helper used by the scheduler.
 */
export function BirthdayBanner({ agent }) {
  const isBirthday = useMemo(() => {
    if (!agent?.date_of_birth) return false;
    const dob = String(agent.date_of_birth).slice(0, 10); // YYYY-MM-DD
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) return false;
    const [, m, d] = dob.split('-');

    // Today in America/New_York
    const nowNY = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/New_York',
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date()); // e.g. "2026-08-20"
    const [, todayM, todayD] = nowNY.split('-');
    return m === todayM && d === todayD;
  }, [agent?.date_of_birth]);

  if (!isBirthday) return null;

  const name = [agent.first_name, agent.last_name].filter(Boolean).join(' ').trim() || 'Agent';
  const dob = String(agent.date_of_birth).slice(0, 10);
  const turningAge = (() => {
    try {
      const [birthY] = dob.split('-').map(Number);
      const todayY = Number(new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/New_York', year: 'numeric',
      }).format(new Date()));
      return todayY - birthY;
    } catch { return null; }
  })();

  return (
    <div
      className="mb-4 rounded-lg border border-rose-200 bg-gradient-to-r from-rose-50 via-amber-50 to-rose-50 px-4 py-3 shadow-sm"
      data-testid="agent-birthday-banner"
    >
      <div className="flex items-center gap-3">
        <div className="shrink-0 h-10 w-10 rounded-full bg-rose-100 border border-rose-200 flex items-center justify-center">
          <Cake size={20} className="text-rose-600" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-rose-800 font-semibold">
            <PartyPopper size={16} className="text-amber-500" />
            <span className="text-sm sm:text-base">Happy Birthday, {name}!</span>
          </div>
          <div className="text-[11px] text-rose-700/80">
            {turningAge ? `Turning ${turningAge} today` : 'Wishing you a great day today'}
            {' · '}from the KR Agency Hub team.
          </div>
        </div>
      </div>
    </div>
  );
}
